import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavHeader from "@/components/NavHeader";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Users, Skull, Key, LogIn, ScrollText } from "lucide-react";

export default function Chronicles() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [joinCode, setJoinCode] = useState("");

  const load = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const r = await api.get("/chronicles");
      setList(r.data);
    } catch {
      toast.error("Failed to load chronicles");
    }
    setBusy(false);
  };

  useEffect(() => { if (!loading) load(); }, [loading, user]);

  const create = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    try {
      const r = await api.post("/chronicles", { name: name.trim(), description: description.trim() });
      toast.success(`CHRONICLE FORGED · CODE ${r.data.code}`);
      setShowCreate(false); setName(""); setDescription("");
      navigate(`/chronicle/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const join = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) { toast.error("Enter a code"); return; }
    try {
      const r = await api.post("/chronicles/join", { code });
      toast.success(r.data.already_member ? "REJOINED CHRONICLE" : "JOINED CHRONICLE");
      setShowJoin(false); setJoinCode("");
      navigate(`/chronicle/${r.data.chronicle_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Join failed");
    }
  };

  if (!loading && !user) {
    return (
      <div className="min-h-screen scanlines">
        <NavHeader />
        <main className="max-w-[900px] mx-auto px-4 sm:px-6 py-16 text-center">
          <ScrollText className="mx-auto text-[color:var(--accent-magenta)]" size={64} />
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter mt-4">
            CHRONICLES <span className="text-[color:var(--accent-yellow)]">REQUIRE</span> A SIGNAL
          </h1>
          <p className="font-mono text-sm text-[color:var(--text-secondary)] mt-3 max-w-xl mx-auto">
            Multiplayer chronicles need cloud sync. Sign in to create a campaign, invite players by code, and share the doom.
          </p>
          <button onClick={login} data-testid="chronicles-signin-btn" className="cy-btn cy-btn-solid-yellow mt-6">
            <LogIn size={16} className="mr-2" /> SIGN IN TO CONTINUE
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen scanlines">
      <NavHeader />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="tape tape-magenta">// CAMPAIGNS</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter">
              YOUR <span className="text-[color:var(--accent-magenta)]">CHRONICLES</span>
            </h1>
            <p className="font-mono text-sm text-[color:var(--text-secondary)] mt-1">
              Rally your crew · share a code · run the doom together.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="join-chronicle-btn"
              onClick={() => setShowJoin(true)}
              className="cy-btn cy-btn-cyan"
            >
              <Key size={16} className="mr-1" /> JOIN
            </button>
            <button
              data-testid="new-chronicle-btn"
              onClick={() => setShowCreate(true)}
              className="cy-btn cy-btn-solid-yellow"
            >
              <Plus size={18} className="mr-1" /> NEW CHRONICLE
            </button>
          </div>
        </div>

        {busy && <div className="font-mono text-sm text-[color:var(--text-muted)]">LOADING…</div>}

        {!busy && list.length === 0 && (
          <div className="panel p-8 text-center">
            <Users className="mx-auto text-[color:var(--accent-cyan)]" size={48} />
            <div className="font-display text-2xl uppercase font-black mt-3">NO CHRONICLES YET.</div>
            <div className="font-mono text-sm text-[color:var(--text-secondary)] mt-2">
              Start one and share the code, or paste a friend&apos;s code to jump in.
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-6">
              <button onClick={() => setShowCreate(true)} data-testid="empty-new-chronicle-btn" className="cy-btn cy-btn-solid-yellow">
                <Plus size={16} className="mr-1" /> FORGE CHRONICLE
              </button>
              <button onClick={() => setShowJoin(true)} data-testid="empty-join-chronicle-btn" className="cy-btn cy-btn-cyan">
                <Key size={16} className="mr-1" /> ENTER CODE
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="chronicle-grid">
          {list.map((c) => {
            const isOwner = user && c.owner_user_id === user.user_id;
            const isGm = user && (c.gms || []).includes(user.user_id);
            return (
              <div
                key={c.id}
                className="panel panel-cyan p-5 hover:panel-yellow transition-all cursor-pointer"
                data-testid={`chronicle-card-${c.id}`}
                onClick={() => navigate(`/chronicle/${c.id}`)}
              >
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--accent-cyan)] truncate">
                      {isOwner ? "// OWNER" : isGm ? "// GM" : "// PLAYER"}
                    </div>
                    <div className="font-display text-2xl font-black uppercase tracking-tight truncate">
                      {c.name || "UNNAMED"}
                    </div>
                  </div>
                  <div className="tape tape-magenta text-[10px] whitespace-nowrap">{c.code}</div>
                </div>
                {c.description && (
                  <div className="font-mono text-[11px] text-[color:var(--text-secondary)] italic mb-3 line-clamp-2">
                    {c.description}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-1 text-center text-[10px] font-mono">
                  <div className="border-2 border-[color:var(--border-hard)] p-1">
                    <div className="text-[8px] uppercase text-[color:var(--text-muted)]">CREW</div>
                    <div className="font-display text-base text-[color:var(--accent-yellow)]">{(c.members || []).length}</div>
                  </div>
                  <div className="border-2 border-[color:var(--border-hard)] p-1">
                    <div className="text-[8px] uppercase text-[color:var(--text-muted)]">GMS</div>
                    <div className="font-display text-base text-[color:var(--accent-magenta)]">{(c.gms || []).length}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CREATE MODAL */}
        {showCreate && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
            <div className="panel panel-yellow p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <div className="tape tape-magenta mb-3">// NEW CHRONICLE</div>
              <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4">
                FORGE A CAMPAIGN
              </h2>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">NAME</label>
              <input
                data-testid="chronicle-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="RUST CATHEDRAL RUN"
                maxLength={80}
                className="cy-input w-full mt-1 mb-3"
                autoFocus
              />
              <label className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">DESCRIPTION</label>
              <textarea
                data-testid="chronicle-desc-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Neon sprawl heist · session 1..."
                maxLength={500}
                rows={3}
                className="cy-input w-full mt-1 mb-4"
              />
              <div className="flex items-center gap-2 justify-end">
                <button onClick={() => setShowCreate(false)} className="cy-btn text-xs">CANCEL</button>
                <button data-testid="chronicle-create-submit" onClick={create} className="cy-btn cy-btn-solid-yellow">
                  <Plus size={14} className="mr-1" /> FORGE
                </button>
              </div>
            </div>
          </div>
        )}

        {/* JOIN MODAL */}
        {showJoin && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowJoin(false)}>
            <div className="panel panel-magenta p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <div className="tape tape-cyan mb-3">// JOIN CHRONICLE</div>
              <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4">
                ENTER THE CODE
              </h2>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">6-CHAR CODE</label>
              <input
                data-testid="chronicle-join-input"
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase().replace(/\s/g, ""))}
                placeholder="ABC123"
                maxLength={16}
                className="cy-input w-full mt-1 mb-4 font-display text-2xl tracking-widest"
                autoFocus
              />
              <div className="flex items-center gap-2 justify-end">
                <button onClick={() => setShowJoin(false)} className="cy-btn text-xs">CANCEL</button>
                <button data-testid="chronicle-join-submit" onClick={join} className="cy-btn cy-btn-cyan">
                  <LogIn size={14} className="mr-1" /> JACK IN
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
